import React, { useEffect, useState } from 'react';
import { Table, Button, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useAuth0 } from '@auth0/auth0-react';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';
import { theme } from '../../../theme';
import { ActionButtons } from '../../common';
import SectorModal from './SectorModal';

interface SectorData {
  id: number;
  descripcion: string;
  alta: string;
}

/*let desired_output = (data: any, column: any) => {
  let unique_values = data
      .map((item: any) => item[column])
      .filter(
          (value: any, index: any, current_value: any) => current_value.indexOf(value) === index
      );
  return unique_values;
};
*/


const ListarSectores: React.FC = () => {

  const [data, setData] = useState<SectorData[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [sectorEditar, setSectorEditar] = useState<{ id: number; descripcion: string; alta?: number } | null>(null);

  const { getAccessTokenSilently , isLoading, user } = useAuth0();
  const empresa = user?.id_empresa;
  const sucursal = user?.id_sucursal;

 /* const getTipoText = (tipo: number) => {
    switch (tipo) {
      case 1:
        return 'Grupo';
      case 2:
        return 'Subgrupo';
      case 3:
        return 'Marca';
      default:
        return 'Desconocido';
    }
  };*/    

/*  const altaUnica = desired_output(data, 'alta')
  .map((alta: any) => ({text: alta, value: alta}));
*/

  const columns: ColumnsType<SectorData> = [
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>ID</span>,
      dataIndex: 'id',
      key: 'id',
      width: 80,
      align: 'center' as const,
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Descripción</span>,
      dataIndex: 'descripcion',
      key: 'descripcion',
      render: (text: string) => (
        <span style={{ color: theme.colors.textPrimary, fontWeight: 500 }}>{text}</span>
      ),
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Estado</span>,
      dataIndex: 'alta',
      key: 'alta',
      align: 'center' as const,
      width: 120,
      render: (value: number) => {
        const isActivo = value === 1;
        return (
          <span style={{ 
            display: 'inline-block',
            padding: '4px 12px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: 600,
            backgroundColor: isActivo ? '#d4edda' : '#f8d7da',
            color: isActivo ? theme.colors.success : theme.colors.error
          }}>
            {isActivo ? 'Activo' : 'Inactivo'}
          </span>
        );
      },
      filters: [
        { text: 'Activo', value: 1 },
        { text: 'Inactivo', value: 0 }
      ],
      onFilter: (value, record) => record.alta === value
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Acciones</span>,
      key: 'acciones',
      align: 'center' as const,
      width: 120,
      render: (_, record) => (
        <ActionButtons
          onEdit={() => handleEditar(record)}
          onDelete={() => handleEliminar(record.id)}
          tooltips={{
            edit: 'Editar sector',
            delete: 'Eliminar sector'
          }}
        />
      ),
    }
  ];

  const fetchSectores = async () => {
    setLoading(true);
    try {
      const accessToken = await getAccessTokenSilently();
      const response = await fetch(`${process.env.REACT_APP_INSUMOS_URL}/listar_sectores/${empresa}/${sucursal}`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Error al cargar los sectores');
      }  
      const result = await response.json();   
      setData(result);
    } catch (error) {
      console.error('Error completo:', error);
      message.error('Error al cargar los sectores');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isLoading) {  // Only fetch when Auth0 has finished loading
      fetchSectores();
    }
  }, [empresa, sucursal, isLoading]); // Add dependencies

  const handleNuevo = () => {
    setSectorEditar(null);
    setModalVisible(true);
  };

  const handleEditar = (sector: SectorData) => {
    setSectorEditar({ 
      id: sector.id, 
      descripcion: sector.descripcion,
      alta: Number(sector.alta) // Convertir a número
    });
    setModalVisible(true);
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setSectorEditar(null);
  };

  const handleModalSuccess = () => {
    fetchSectores(); // Recargar lista
  };

  const handleEliminar = async (id: number) => {
    if (window.confirm('¿Está seguro de que desea eliminar este sector?')) {
      try {
        const accessToken = await getAccessTokenSilently();
        const response = await fetch(`${process.env.REACT_APP_INSUMOS_URL}/eliminar_sector/${id}/${empresa}/${sucursal}/`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        });
        
        if (!response.ok) {
          throw new Error('Error al eliminar el sector');
        }
        
        message.success('Sector eliminado correctamente');
        fetchSectores(); // Recargar datos
      } catch (error) {
        console.error('Error al eliminar:', error);
        message.error('Error al eliminar el sector');
      }
    }
  };

  return (
    <div>
      {/* Botón Nuevo arriba */}
      <div style={{ marginBottom: '16px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
        <Button 
          type='primary'
          onClick={handleNuevo}
          style={{
            backgroundColor: theme.colors.secondary,
            borderColor: theme.colors.secondary,
            fontWeight: 500
          }}
        >
          Nuevo Sector
        </Button>
      </div>
      
      {/* Tabla con acciones inline */}
      <Table
        scroll={{ x: 'max-content' }}
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={{
          pageSize: 10,
          total: data.length,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} registros`,
        }}
      />

      {/* Modal para crear/editar */}
      <SectorModal
        visible={modalVisible}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
        sector={sectorEditar}
      />
    </div>
  );
};

export default ListarSectores; 